import { internalError } from './constants';

import {
  errorTracker, eventTracker,
  datadogTracker, heapTracker, mockTracker
} from './trackers';

import type { Tracker, CreateTimeEvent, TrackError, TrackEvent, TrackPerformance } from './types/tracker';

import * as heap from '../heap/index';
import { addConsentListener, getConsentState } from '../oneTrust/oneTrust';
import { InitializeParams } from './types/types';

let heapConsentListenerId: Symbol | null = null;
function initialize(params: InitializeParams) {
  const { trackingData } = params;

  if (getConsentState().hasAnalyticsConsent) {
    heap.initializeHeap(trackingData);
  }

  if (!heapConsentListenerId) {
    heapConsentListenerId = addConsentListener((hasAnalyticsConsent) => {
      if (hasAnalyticsConsent) heap.initializeHeap(trackingData);
      else heap.tearDown();
    });
  }
}

function tearDown() {
  heap.tearDown();
}

const initializeTrackers = (trackers: Tracker[]) => {
  window.monitoring = {
    timeEvents: new Map(),
    selectedTrackers: new Set<Tracker>()
  }; 
  trackers.forEach(selectedTracker => {
    if (selectedTracker.init()) {
      window.monitoring.selectedTrackers.add(selectedTracker);
    }
  });
  return window.monitoring.selectedTrackers;
};

const createTimeEvent = ({ eventName, time = Date.now(), details = {} }: CreateTimeEvent) => {
  if (!window.monitoring || !window.monitoring.timeEvents) {
    return { error: internalError.createTimeEvent.init };
  }
  const { timeEvents } = window.monitoring;

  if (typeof eventName !== 'string') {
    return { error: internalError.createTimeEvent.eventName };
  }
  if (typeof time !== 'number') {
    return { error: internalError.createTimeEvent.time };
  }
  timeEvents.set(eventName, { time, details });

  return { timeEvent: timeEvents.get(eventName) };
};

const trackError = async ({ error, details = {}}: TrackError): Promise<unknown> => {
  const { selectedTrackers } = window.monitoring;

  if (!(error instanceof Error)) {
    return Promise.reject(internalError.trackError.errorObject);
  }  
  return Promise.all(Array.from(selectedTrackers).map(
    selectedTracker => errorTracker(selectedTracker, { name: 'Error', message: error.message }, details)
  ));
};

const trackEvent = async ({ eventName, details = {}}: TrackEvent): Promise<unknown> => {
  const { selectedTrackers } = window.monitoring;

  if (typeof eventName !== 'string') {
    return Promise.reject(internalError.trackEvent.eventName);
  }
  return Promise.all(Array.from(selectedTrackers).map(
    selectedTracker => eventTracker(selectedTracker, eventName, details)
  ));
};

const trackPerformance = async (
  { eventName, time = Date.now(), details = {}, options = {} }: TrackPerformance
) => {
  if (!window.monitoring || !window.monitoring.timeEvents) {
    return Promise.reject(internalError.trackPerformance.init);
  }
  const { timeEvents, selectedTrackers } = window.monitoring;

  if (typeof eventName !== 'string') {
    return Promise.reject(internalError.trackPerformance.eventName);
  }
  const timeEvent = timeEvents.get(eventName);

  if (typeof timeEvent === 'undefined') {
    return Promise.reject(internalError.trackPerformance.timeEvent);
  }

  if (timeEvents.has(eventName)) {
    const result = await Promise.all(Array.from(selectedTrackers).map(
      selectedTracker => eventTracker(selectedTracker, eventName, {
        ...details,
        ...timeEvent.details,
        duration: `${time - timeEvent.time} ms`
      })
    ));
    if (options.removeAfterRead) {
      timeEvents.delete(eventName);
    }
    return Promise.resolve(result);
  }
  return Promise.resolve(null);
};

export {
  initialize,
  internalError,
  initializeTrackers,
  datadogTracker,
  heapTracker,
  mockTracker,
  createTimeEvent,
  tearDown,
  trackError,
  trackEvent,
  trackPerformance,
};
